import { Component, Injector, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { BaseEntityComponent, BnDateUtils, messageLevel } from "@bndes/bndes-ux4/bndes-angular-utils";
import { BnConfirmDirective } from "@bndes/bndes-ux4/bn-confirm";
import { BnModalMessageService } from "@bndes/bndes-ux4/bn-modal-message";
import { BnUsuarioService, Usuario } from "@bndes/bndes-ux4/bn-usuario";
import { Observable, of, Subject } from 'rxjs';
import { map, mergeMap, takeUntil } from 'rxjs/operators';
import { ROLE_GESTOR_ENVIO } from "src/app/constantes/permissao.constants";
import { CanComponentDeactivate } from "src/app/core/can-deactivate-guard.service";
import { AgenteFinanceiro } from "src/app/model/agente-financeiro.model";
import { InadimplenciaTecnica } from "src/app/model/inadimplencia-tecnica.model";
import { PermissaoService } from 'src/app/shared/permissao.service';
import { RemoverAlertasService } from "src/app/shared/services/remover-alertas.service";
import { InadimplenciaService } from "../inadimplencia.service";
import { DataSistemaService } from "src/app/shared/services/data-sistema.service";
import moment from "moment";

@Component({
    selector: 'app-inadimplencia-cadastro',
    templateUrl: './inadimplencia-cadastro.component.html',
    standalone: false
})
export class InadimplenciaCadastroComponent
        extends BaseEntityComponent<InadimplenciaTecnica> implements CanComponentDeactivate, OnInit, OnDestroy {

    public formInadimplencia:UntypedFormGroup;
    public agentesFinanceiros:AgenteFinanceiro[]=[];
    public ocorrencias:any[] = [];
    private usuarioLogado:Usuario;
    private vc: BnConfirmDirective;
    dataAmanha: Date = new Date();
    private readonly destroy$ = new Subject();

    constructor(
        private activatedRoute:ActivatedRoute,
        private modalService: BnModalMessageService,
        private permissaoService: PermissaoService,
        protected injector: Injector,
        private usuarioService: BnUsuarioService,
        private inadimplenciaService: InadimplenciaService,
        private removerAlertasService: RemoverAlertasService,
        private dataSistema: DataSistemaService) {
        super(inadimplenciaService);
        this.config.rotaLista = "inadimplencia/pesquisa";
        this.agentesFinanceiros = this.activatedRoute.snapshot.data['agentes'];
        this.dataAmanha = moment(this.dataAmanha).add(1, 'days').toDate();
    }

    async buscaDataSistemaESetaDataAmanha() {
      const dataHoje = await this.dataSistema.getDataCorrente().toPromise();
      this.dataAmanha = moment(dataHoje).add(1, 'days').toDate();
    }

    form(): UntypedFormGroup {
        return this.formBuilder
        .group({
            agenteFinanceiro:[{
                value: null,
                disabled: !this.modoEdicao},
                Validators.required,
            ],

            dataInicio:[{
                value: null,
                disabled: !this.modoEdicao},
                Validators.required,
            ],

            dataFim:[{
                value: null,
                disabled: !this.modoEdicao},
            ],

            motivo:[{
                value: null,
                disabled:!this.modoEdicao},
                Validators.compose([Validators.required,Validators.minLength(10)]),
            ],

            observacoes:[{
                value: null,
                disabled:!this.modoEdicao},
            ],

            tipoBloqueio:[{
              value: null,
              disabled: !this.modoEdicao},
              Validators.required,
          ],

        },);
    }

    alteraForm(model:InadimplenciaTecnica){

        this.formInadimplencia.patchValue({
            agenteFinanceiro: model.agenteFinanceiro,
            dataInicio: this.ajustaCampoDate(model.dataInicio),
            dataFim: model.dataFim ? this.ajustaCampoDate(model.dataFim) : null,
            motivo: model.motivo || '',
            observacoes: model.observacoes || '',
            tipoBloqueio: model.tipoBloqueio || '',
        });

      if (model.id != null) {
        this.formInadimplencia.get('agenteFinanceiro').disable();
        this.verificaEDesabilitaDataInicioCasoVigente()
      }
    }

    private async verificaEDesabilitaDataInicioCasoVigente(): Promise<void> {
      const dataHoje = await this.dataSistema.getDataCorrente().toPromise();
      const dataHojeFormatada = moment(dataHoje)
      const dataInicio = moment(this.formInadimplencia.get('dataInicio').value);

      if (dataInicio <= dataHojeFormatada) {
        this.formInadimplencia.get('dataInicio').disable();
      }
    }

    salvarDesabilitado(): boolean {
      return this.formInadimplencia.pristine || !this.formInadimplencia.valid
    }

    ngOnInit(){
      this.buscaDataSistemaESetaDataAmanha();
        this.permissaoService
            .getPermissoesUsuarioAutenticado()
            .pipe(takeUntil(this.destroy$))
            .subscribe(resp => {
                this.modoEdicao = resp && resp[ROLE_GESTOR_ENVIO];
                if (this.modoEdicao) {
                    this.usuarioService.usuario$.pipe(takeUntil(this.destroy$)).subscribe(usuario => this.usuarioLogado = usuario);
                }
                this.preparaTela().pipe(takeUntil(this.destroy$)).subscribe((inadimplencia) => {
                  this.setModel(inadimplencia);
                  this.alteraForm(this.model);
                });
            });
    }

    ngOnDestroy(): void {
      this.destroy$.next(undefined);
      this.destroy$.complete();
    }


    preparaTela(): Observable<InadimplenciaTecnica> {
      this.formInadimplencia = this.form();
      return this.route.params.pipe(
        mergeMap((params) => {
          if (params['id']) {
            return this.inadimplenciaService.findById(params['id']);
          } else {
            const novo: InadimplenciaTecnica = this.inadimplenciaService.getNewEntity();
            novo.nomeUsuarioInsercao = this.usuarioLogado.nome;
            return of(novo);
          }
        })
      );
    }

    private getHashFromDados(inadimplencia: InadimplenciaTecnica): string {
      const dataInicio = moment(inadimplencia.dataInicio).format('YYYYMMDD');
      const dataFim = moment(inadimplencia.dataFim).format('YYYYMMDD');
      return btoa(`${dataInicio}:${dataFim}:${inadimplencia.motivo}:${inadimplencia.observacoes}:${inadimplencia.tipoBloqueio}`);
    }

    salvar() {
        this.antesDeSalvar();
        const cadastro =  {
          dataInicio: this.formInadimplencia.get('dataInicio').value,
          dataFim: this.formInadimplencia.get('dataFim').value,
          motivo: this.formInadimplencia.get('motivo').value,
          observacoes: this.formInadimplencia.get('observacoes').value,
          tipoBloqueio: this.formInadimplencia.get('tipoBloqueio').value,
          agenteFinanceiro: this.formInadimplencia.get('agenteFinanceiro').value,
        }  as InadimplenciaTecnica

        const hashAntesAlteracao = this.getHashFromDados(this.model);
        const hashDepoisAlteracao = this.getHashFromDados(cadastro);

        if (hashAntesAlteracao === hashDepoisAlteracao) {
          this.messageService.addMessage({
            title: 'Aviso',
            type: 'CRUD_OPERATION',
            level: messageLevel.INFO,
            message: 'Nenhuma alteração foi realizada nos campos.'
          });
          this.modoEdicao = false;
          this.formInadimplencia.markAsPristine();
          this.formInadimplencia.disable();
          return;
        }

        this.model.agenteFinanceiro = cadastro.agenteFinanceiro;
        this.model.dataInicio = cadastro.dataInicio;
        this.model.dataFim = cadastro.dataFim;
        this.model.motivo = cadastro.motivo;
        this.model.observacoes = cadastro.observacoes;
        this.model.tipoBloqueio = cadastro.tipoBloqueio;

        this.ocorrencias = [];
        this.removerAlertasService.removerTodos();
        this.inadimplenciaService['save'](this.getModel()).pipe(takeUntil(this.destroy$)).subscribe(
            (inadimplenciaSalva) => {
                this.setModel(inadimplenciaSalva);
                this.alteraForm(this.model);
                this.aposSalvar();
            },
            (error) => {
                console.log(error);
                this.ocorrencias = this.inadimplenciaService.ocorrencias;
        });
    }

    aposSalvar() {
        this.exibeMensagemSucesso();
        this.modoEdicao = false;
        this.formInadimplencia.markAsPristine();
        this.formInadimplencia.disable();
    }

    exibeMensagemSucesso(): void {
        this.messageService.addMessage({
            title: 'Sucesso',
            type: 'CRUD_OPERATION',
            level: messageLevel.SUCCESS,
            message: 'Inadimplemento Operacional criado/atualizado com sucesso.'
        });
    }

    async verificaSePodeSair(): Promise<boolean> {
      const titulo = 'Confirmação';
      const mensagem = this.model.id!=null ? 'Descartar alterações realizadas no inadimplemento operacional ?' : 'Descartar novo inadimplemento operacional ?';

      try {
          const resultadoConfimacao = await this.modalService.abrirConfirm(titulo, mensagem).result;
          return resultadoConfimacao.value === 'ok';
      } catch (e) {
          return false;
      }
    }

    canDeactivate(): Promise<boolean> | boolean {
        if (!this.modoEdicao || this.formInadimplencia.pristine)
          return true; 
        
        return this.verificaSePodeSair();
    }


    ajustaCampoDate(data:any) {
        if (typeof data === 'string') {
          if (data.length == 10) {
            data += ' 00:00:00';
          }
          return new Date(data);
        }

        return data;
      }
}
